<template>
    <div class="min-h-screen w-full flex flex-col bg-gradient-to-br from-gray-100 to-blue-50">
        <div class="container mx-auto">
            <div class="px-4 sm:px-8 flex flex-col w-full pb-4 sm:pb-8 mt-4 sm:mt-8">
                <div class="mb-4">
                    <bread-crumb :items="breadCrumbItems"></bread-crumb>
                </div>

                <div v-if="pageLoading" class="flex justify-center py-16">
                    <base-page-spinner type="spin" class-list="h-16"></base-page-spinner>
                </div>

                <template v-else>
                <section class="res-hero w-full rounded-2xl border shadow-lg p-6 md:p-8 mb-6">
                    <div class="flex flex-col gap-6">
                        <div class="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                            <div class="min-w-0 flex-1">
                                <p class="text-xs font-semibold uppercase tracking-wide text-amber-700 mb-2">
                                    Library · {{ asOf }}
                                </p>
                                <h1 class="text-2xl md:text-3xl font-bold text-gray-900">Library</h1>
                                <p class="mt-2 text-gray-600 max-w-2xl leading-relaxed">
                                    Facilities, databases, print inventory, and digital collections for
                                    {{ campusTitle }}.
                                </p>
                            </div>
                            <div class="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 shrink-0">
                                <button
                                    type="button"
                                    class="lib-export-btn inline-flex items-center justify-center px-4 py-2.5 rounded-lg text-sm font-semibold"
                                    :disabled="pageLoading"
                                    @click="exportAllExcel"
                                >
                                    <font-awesome-icon :icon="['fas', 'file-excel']" class="mr-2" />
                                    Export Excel
                                </button>
                                <a
                                    v-if="portalUrl"
                                    :href="portalUrl"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    class="portal-btn inline-flex items-center justify-center px-4 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold shadow-sm"
                                >
                                    {{ portalLabel }}
                                    <font-awesome-icon :icon="['fas', 'external-link-alt']" class="ml-2 text-xs" />
                                </a>
                            </div>
                        </div>

                        <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <div class="kpi-mini">
                                <div class="kpi-mini-label">Digital</div>
                                <div class="kpi-mini-value">{{ formatNumber(digitalTotal) }}</div>
                            </div>
                            <div class="kpi-mini">
                                <div class="kpi-mini-label">Print</div>
                                <div class="kpi-mini-value">{{ formatNumber(campusPrintTotal) }}</div>
                            </div>
                            <div class="kpi-mini">
                                <div class="kpi-mini-label">Seating</div>
                                <div class="kpi-mini-value">{{ seatingCapacity }}</div>
                            </div>
                        </div>
                    </div>
                </section>

                <!-- Facilities -->
                <section class="bg-white rounded-2xl border shadow-lg p-6 md:p-8 mb-6">
                    <div class="flex items-center justify-between mb-5">
                        <h2 class="text-lg font-semibold text-gray-800 flex items-center">
                            <font-awesome-icon :icon="['fas', 'building']" class="text-amber-600 mr-2" />
                            {{ facilitiesLabel }}
                        </h2>
                        <span v-if="!hasFacilities" class="text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded-full">
                            Coming soon for this campus
                        </span>
                    </div>

                    <div v-if="hasFacilities" class="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
                        <div
                            v-for="item in facilityItems"
                            :key="item.key"
                            class="facility-card"
                        >
                            <div class="facility-icon">
                                <font-awesome-icon :icon="['fas', item.icon]" />
                            </div>
                            <div>
                                <div class="text-2xl font-bold text-gray-900">{{ formatFacilityValue(item.value) }}</div>
                                <div class="text-xs text-gray-500 mt-1 leading-snug">
                                    {{ item.label }}
                                    <span v-if="item.detail" class="block text-gray-400">{{ item.detail }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-else class="text-center py-10 text-gray-500 text-sm">
                        Facility holdings for {{ campusTitle }} will be added in a future update.
                    </div>
                </section>

                <!-- Databases / Subscriptions -->
                <section class="db-block bg-white rounded-2xl border shadow-lg p-6 md:p-8 mb-6">
                    <div class="flex items-center justify-between mb-5">
                        <div>
                            <h2 class="text-lg font-semibold text-gray-800 flex items-center">
                                <font-awesome-icon :icon="['fas', 'database']" class="text-indigo-600 mr-2" />
                                Library Databases
                            </h2>
                            <p v-if="hasDatabases" class="text-xs text-gray-500 mt-1">
                                Shared + campus subscriptions · {{ databases.length }} titles · {{ asOf }}
                            </p>
                        </div>
                        <span
                            v-if="!hasDatabases"
                            class="text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded-full"
                        >
                            No databases yet
                        </span>
                    </div>

                    <template v-if="hasDatabases">
                    <div class="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-5">
                        <div class="grid grid-cols-2 md:grid-cols-4 gap-2 w-full lg:w-auto lg:ml-auto">
                            <div class="stat-tile">
                                <div class="stat-label">Total</div>
                                <div class="stat-value">{{ databases.length }}</div>
                            </div>
                            <div class="stat-tile">
                                <div class="stat-label">Subscribed</div>
                                <div class="stat-value">{{ subscribedCount }}</div>
                            </div>
                            <div class="stat-tile">
                                <div class="stat-label">Open access</div>
                                <div class="stat-value">{{ openAccessCount }}</div>
                            </div>
                            <div class="stat-tile">
                                <div class="stat-label">With AI</div>
                                <div class="stat-value">{{ aiCount }}</div>
                            </div>
                        </div>
                    </div>

                    <div class="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-6">
                        <section class="rounded-xl border border-slate-100 bg-slate-50/50 p-4 xl:col-span-1">
                            <h3 class="text-sm font-semibold text-gray-700 mb-2">Access mix</h3>
                            <apexchart
                                type="donut"
                                height="240"
                                :options="statusDonutOptions"
                                :series="statusDonutSeries"
                            />
                        </section>
                        <section class="rounded-xl border border-slate-100 bg-slate-50/50 p-4 xl:col-span-2">
                            <h3 class="text-sm font-semibold text-gray-700 mb-2">Access channel</h3>
                            <apexchart
                                type="bar"
                                height="240"
                                :options="accessBarOptions"
                                :series="accessBarSeries"
                            />
                        </section>
                    </div>

                    <div class="flex flex-col md:flex-row md:items-center gap-3 mb-5">
                        <div class="flex-1">
                            <input
                                v-model="searchText"
                                type="search"
                                placeholder="Search databases..."
                                class="w-full px-4 py-2 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                            />
                        </div>
                        <div class="flex flex-wrap gap-2">
                            <button
                                v-for="f in dbFilters"
                                :key="f.key"
                                type="button"
                                class="filter-chip"
                                :class="{ active: activeFilter === f.key }"
                                @click="activeFilter = f.key"
                            >
                                {{ f.label }}
                            </button>
                        </div>
                    </div>

                    <div class="grid grid-cols-1 lg:grid-cols-2 gap-5">
                        <article
                            v-for="item in filteredDatabases"
                            :key="item.id"
                            class="db-card"
                        >
                            <div class="flex items-start justify-between gap-3">
                                <div>
                                    <div class="text-xs text-gray-400 mb-1">#{{ item.id }}</div>
                                    <h3 class="text-base font-semibold text-gray-900 leading-snug">{{ item.name }}</h3>
                                </div>
                                <span
                                    class="status-pill whitespace-nowrap"
                                    :class="item.status === 'open_access' ? 'status-open' : 'status-sub'"
                                >
                                    {{ item.status === "open_access" ? "Open Access" : "Subscribed" }}
                                </span>
                            </div>
                            <p class="mt-3 text-sm text-gray-600 leading-relaxed">
                                {{ truncatedDescription(item) }}
                            </p>
                            <button
                                v-if="needsExpand(item)"
                                type="button"
                                class="mt-2 text-xs font-semibold text-indigo-600 hover:text-indigo-800"
                                @click="toggleExpand(item.id)"
                            >
                                {{ expanded[item.id] ? "Show less" : "Read more" }}
                            </button>
                            <div class="mt-4 flex flex-wrap gap-2">
                                <span v-if="item.hasAi" class="badge badge-ai">
                                    <font-awesome-icon :icon="['fas', 'magic']" class="mr-1" />
                                    AI features
                                </span>
                                <span v-if="item.sharedWith" class="badge badge-shared">
                                    Shared with {{ item.sharedWith }}
                                </span>
                                <span class="badge badge-access">{{ item.access }}</span>
                            </div>
                        </article>
                    </div>

                    <div
                        v-if="!filteredDatabases.length"
                        class="text-center text-sm text-gray-500 py-8"
                    >
                        No databases match your filters.
                    </div>

                    <div class="mt-6 overflow-x-auto">
                        <h3 class="text-sm font-semibold text-gray-800 mb-3">Directory</h3>
                        <table class="min-w-full text-sm">
                            <thead class="bg-slate-50 text-gray-600">
                                <tr>
                                    <th class="px-3 py-3 text-left">#</th>
                                    <th class="px-3 py-3 text-left">Database</th>
                                    <th class="px-3 py-3 text-left">Access</th>
                                    <th class="px-3 py-3 text-left">Subscription</th>
                                    <th class="px-3 py-3 text-left">Flags</th>
                                </tr>
                            </thead>
                            <tbody class="divide-y divide-gray-100">
                                <tr
                                    v-for="item in filteredDatabases"
                                    :key="'row-' + item.id"
                                    class="hover:bg-slate-50"
                                >
                                    <td class="px-3 py-3 text-gray-400">{{ item.id }}</td>
                                    <td class="px-3 py-3 font-medium text-gray-800">{{ item.name }}</td>
                                    <td class="px-3 py-3 text-gray-600">{{ item.access }}</td>
                                    <td class="px-3 py-3">
                                        <span
                                            class="status-pill"
                                            :class="item.status === 'open_access' ? 'status-open' : 'status-sub'"
                                        >
                                            {{ item.subscriptionLabel }}
                                        </span>
                                    </td>
                                    <td class="px-3 py-3">
                                        <span v-if="item.hasAi" class="text-indigo-600 text-xs font-semibold mr-2">AI</span>
                                        <span v-if="item.sharedWith" class="text-amber-700 text-xs">Shared</span>
                                        <span v-if="!item.hasAi && !item.sharedWith" class="text-gray-300">—</span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    </template>
                    <div v-else class="text-center py-10 text-gray-500 text-sm">
                        No library databases configured yet. Use Manage Sections to add subscriptions.
                    </div>
                </section>

                <!-- Charts -->
                <div class="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-6">
                    <section class="bg-white rounded-2xl border shadow-lg p-6">
                        <h2 class="text-lg font-semibold text-gray-800 mb-1">Digital holdings by provider</h2>
                        <p class="text-xs text-gray-500 mb-4">{{ digitalScope }} · Total {{ formatNumber(digitalTotal) }}</p>
                        <apexchart
                            type="bar"
                            height="380"
                            :options="digitalBarOptions"
                            :series="digitalBarSeries"
                        />
                    </section>

                    <section class="bg-white rounded-2xl border shadow-lg p-6">
                        <h2 class="text-lg font-semibold text-gray-800 mb-1">Print holdings by campus</h2>
                        <p class="text-xs text-gray-500 mb-4">AA vs AD · Total {{ formatNumber(printGrandTotal) }}</p>
                        <apexchart
                            type="donut"
                            height="320"
                            :options="printDonutOptions"
                            :series="printDonutSeries"
                        />
                        <div class="grid grid-cols-2 gap-3 mt-2">
                            <div class="rounded-xl bg-slate-50 p-3 text-center">
                                <div class="text-xs text-gray-500">AA</div>
                                <div class="text-xl font-bold text-slate-800">{{ formatNumber(printAa) }}</div>
                            </div>
                            <div class="rounded-xl bg-slate-50 p-3 text-center">
                                <div class="text-xs text-gray-500">AD</div>
                                <div class="text-xl font-bold text-slate-800">{{ formatNumber(printAd) }}</div>
                            </div>
                        </div>
                    </section>
                </div>

                <div class="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-6">
                    <section class="bg-white rounded-2xl border shadow-lg p-6">
                        <h2 class="text-lg font-semibold text-gray-800 mb-1">Print by category</h2>
                        <p class="text-xs text-gray-500 mb-4">
                            Campus {{ campusCode }} · Total {{ formatNumber(campusPrintTotal) }}
                        </p>
                        <apexchart
                            type="bar"
                            height="360"
                            :options="printCategoryOptions"
                            :series="printCategorySeries"
                        />
                    </section>

                    <section class="bg-white rounded-2xl border shadow-lg p-6">
                        <h2 class="text-lg font-semibold text-gray-800 mb-1">Digital by content type</h2>
                        <p class="text-xs text-gray-500 mb-4">
                            {{ digitalScope }} · Total {{ formatNumber(digitalTotal) }}
                        </p>
                        <apexchart
                            type="bar"
                            height="360"
                            :options="digitalTypeOptions"
                            :series="digitalTypeSeries"
                        />
                    </section>
                </div>

                <!-- Tables -->
                <section class="bg-white rounded-2xl border shadow-lg p-6 md:p-8 mb-6">
                    <h2 class="text-lg font-semibold text-gray-800 mb-4">Digital collections by provider</h2>
                    <div class="overflow-x-auto">
                        <table class="min-w-full text-sm">
                            <thead class="bg-slate-50 text-gray-600">
                                <tr>
                                    <th class="px-4 py-3 text-left font-medium">#</th>
                                    <th class="px-4 py-3 text-left font-medium">Provider</th>
                                    <th class="px-4 py-3 text-right font-medium">Items</th>
                                    <th class="px-4 py-3 text-right font-medium">Share</th>
                                </tr>
                            </thead>
                            <tbody class="divide-y divide-gray-100 text-gray-700">
                                <tr v-for="(p, idx) in sortedProviders" :key="p.name" class="hover:bg-slate-50">
                                    <td class="px-4 py-3 text-gray-400">{{ idx + 1 }}</td>
                                    <td class="px-4 py-3 font-medium">{{ p.name }}</td>
                                    <td class="px-4 py-3 text-right tabular-nums">{{ formatNumber(p.total) }}</td>
                                    <td class="px-4 py-3 text-right text-gray-500">
                                        {{ sharePercent(p.total) }}%
                                    </td>
                                </tr>
                            </tbody>
                            <tfoot class="bg-slate-50 font-semibold text-gray-800">
                                <tr>
                                    <td class="px-4 py-3" colspan="2">TOTAL</td>
                                    <td class="px-4 py-3 text-right">{{ formatNumber(digitalTotal) }}</td>
                                    <td class="px-4 py-3 text-right">100%</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                    <p class="mt-3 text-xs text-gray-400">
                        Digital collections cover both AA and AD campuses.
                    </p>
                </section>

                <section class="bg-white rounded-2xl border shadow-lg p-6 md:p-8 mb-6">
                    <h2 class="text-lg font-semibold text-gray-800 mb-4">Print inventory</h2>
                    <div class="overflow-x-auto">
                        <table class="min-w-full text-sm">
                            <thead class="bg-slate-50 text-gray-600">
                                <tr>
                                    <th class="px-4 py-3 text-left font-medium">#</th>
                                    <th class="px-4 py-3 text-left font-medium">Category</th>
                                    <th class="px-4 py-3 text-right font-medium">Count</th>
                                    <th class="px-4 py-3 text-right font-medium">Share</th>
                                </tr>
                            </thead>
                            <tbody class="divide-y divide-gray-100 text-gray-700">
                                <tr
                                    v-for="(cat, idx) in printCategories"
                                    :key="cat.key"
                                    class="hover:bg-slate-50"
                                >
                                    <td class="px-4 py-3 text-gray-400">{{ idx + 1 }}</td>
                                    <td class="px-4 py-3 font-medium">{{ cat.label }}</td>
                                    <td class="px-4 py-3 text-right tabular-nums">
                                        {{ formatCampusPrintValue(cat) }}
                                    </td>
                                    <td class="px-4 py-3 text-right text-gray-500">
                                        {{ campusPrintSharePercent(cat) }}%
                                    </td>
                                </tr>
                            </tbody>
                            <tfoot class="bg-slate-50 font-semibold text-gray-800">
                                <tr>
                                    <td class="px-4 py-3" colspan="2">TOTAL</td>
                                    <td class="px-4 py-3 text-right">{{ formatNumber(campusPrintTotal) }}</td>
                                    <td class="px-4 py-3 text-right">100%</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </section>

                <section class="bg-white rounded-2xl border shadow-lg p-6 md:p-8">
                    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
                        <h2 class="text-lg font-semibold text-gray-800 flex items-center">
                            <font-awesome-icon :icon="['fas', 'book']" class="text-indigo-600 mr-2" />
                            Manage Sections
                        </h2>
                        <span class="text-xs text-gray-500">{{ enabledModuleCount }} sections</span>
                    </div>
                    <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                        <button
                            v-for="module in modules"
                            :key="module.key"
                            type="button"
                            class="lib-module"
                            @click="openModule(module)"
                        >
                            <div class="lib-module__icon">{{ module.icon }}</div>
                            <div class="lib-module__label">{{ module.label }}</div>
                            <div v-if="module.count !== null" class="lib-module__count">
                                {{ module.count }} {{ pluralize(module.unit, module.count) }}
                            </div>
                        </button>
                    </div>
                </section>
                </template>
            </div>
        </div>

        <transition name="fade" mode="out-in">
            <base-full-modal v-if="activeModule" :title="activeModule.label" @close="closeModule">
                <component
                    :is="activeModuleComponent"
                    :key="activeModuleKey"
                    :entity="entity"
                    :campus="campusCode"
                    @changed="onModuleChanged"
                />
            </base-full-modal>
        </transition>
    </div>
</template>

<script>
import { defineAsyncComponent } from "vue";
import gql from "graphql-tag";
import BreadCrumb from "../../../components/shared/BreadCrumb.vue";
import { ADPOLY_ENTITY, getCampusConfig, isValidCampus } from "../config";
import { exportAllLibrarySections, getTotalSheetCount } from "./libraryExport.js";

var CHART_COLORS = [
    "#0f766e",
    "#1d4ed8",
    "#b45309",
    "#7c3aed",
    "#be123c",
    "#0369a1",
    "#4d7c0f",
    "#c2410c",
    "#4338ca",
    "#0e7490",
    "#a16207",
    "#334155",
    "#9f1239",
];

const OVERVIEW_QUERY = gql`
    query LibraryOverview($entity: String!, $campus: String) {
        libraryOverview(entity: $entity, campus: $campus) {
            error
            error_code
            message
            data {
                settings {
                    id
                    portal_url
                    portal_label
                    as_of
                    digital_total
                    digital_scope
                }
                databases {
                    id
                    name
                    description
                    access
                    subscription_label
                    status
                    has_ai
                    shared_with
                    scope
                    campus
                }
                digital_holding_types {
                    id
                    key
                    label
                    total
                }
                digital_holding_providers {
                    id
                    name
                    total
                }
                print_categories {
                    id
                    key
                    label
                    aa_count
                    ad_count
                }
                facilities {
                    id
                    key
                    label
                    value
                    icon
                }
                summary {
                    databases_count
                    subscribed_count
                    open_access_count
                    has_ai_count
                    digital_total
                    print_total
                    facilities_count
                }
            }
        }
    }
`;

var moduleComponentCache = {};

function resolveModuleComponent(key) {
    if (!key) return null;
    if (!moduleComponentCache[key]) {
        moduleComponentCache[key] = defineAsyncComponent(function () {
            return import("./modules/" + key + "Module.vue");
        });
    }
    return moduleComponentCache[key];
}

function mapDatabase(row) {
    return {
        id: row.id,
        name: row.name,
        description: row.description,
        access: row.access,
        subscriptionLabel: row.subscription_label,
        status: row.status,
        hasAi: !!row.has_ai,
        sharedWith: row.shared_with,
        scope: row.scope,
        campus: row.campus,
    };
}

function mapPrintCategory(row) {
    return {
        id: row.id,
        key: row.key,
        label: row.label,
        aa: row.aa_count,
        ad: row.ad_count,
        total: (row.aa_count || 0) + (row.ad_count || 0),
    };
}

export default {
    name: "adpoly-library-index",
    components: { BreadCrumb },
    props: ["campus"],
    data() {
        return {
            entity: ADPOLY_ENTITY,
            pageLoading: false,
            searchText: "",
            activeFilter: "all",
            expanded: {},
            activeModule: null,
            settings: null,
            databasesRaw: [],
            digitalTypes: [],
            digitalProviders: [],
            printCategoriesRaw: [],
            facilitiesRaw: [],
            summary: {
                databases_count: 0,
                subscribed_count: 0,
                open_access_count: 0,
                has_ai_count: 0,
                digital_total: 0,
                print_total: 0,
                facilities_count: 0,
            },
            dbFilters: [
                { key: "all", label: "All" },
                { key: "subscribed", label: "Subscribed" },
                { key: "open_access", label: "Open Access" },
                { key: "ai", label: "AI features" },
                { key: "shared", label: "Shared" },
            ],
            modules: [
                { key: "Settings", label: "Settings", icon: "⚙️", unit: "profile", count: null },
                { key: "Facilities", label: "Facilities", icon: "🏢", unit: "item", count: null },
                { key: "Databases", label: "Databases", icon: "📚", unit: "title", count: null },
                { key: "DigitalHoldings", label: "Digital Holdings", icon: "💾", unit: "record", count: null },
                { key: "PrintHoldings", label: "Print Holdings", icon: "📖", unit: "category", count: null },
            ],
        };
    },
    created() {
        this.validateCampus();
        this.loadOverview();
    },
    watch: {
        campus: function () {
            this.validateCampus();
            this.activeFilter = "all";
            this.searchText = "";
            this.loadOverview();
        },
    },
    computed: {
        role() {
            return this.$store.getters.role;
        },
        permissions() {
            return this.$store.getters.permissions;
        },
        campusConfig() {
            return getCampusConfig(this.campus);
        },
        campusTitle() {
            return this.campusConfig ? this.campusConfig.title : "";
        },
        campusKey() {
            return this.campusConfig ? this.campusConfig.key : "";
        },
        campusCode() {
            return this.campusConfig ? this.campusConfig.campusCode : "";
        },
        enabledModuleCount() {
            return this.modules.length;
        },
        activeModuleComponent() {
            if (!this.activeModule) return null;
            return resolveModuleComponent(this.activeModule.key);
        },
        activeModuleKey() {
            if (!this.activeModule) return "none";
            return this.activeModule.key + "-" + this.campusCode;
        },
        asOf() {
            return this.settings && this.settings.as_of ? this.settings.as_of : "—";
        },
        portalUrl() {
            return this.settings && this.settings.portal_url ? this.settings.portal_url : "";
        },
        portalLabel() {
            return this.settings && this.settings.portal_label
                ? this.settings.portal_label
                : "ADPOLY Library Portal";
        },
        databases() {
            return this.databasesRaw;
        },
        hasDatabases() {
            return this.databases && this.databases.length > 0;
        },
        hasFacilities() {
            return this.facilitiesRaw && this.facilitiesRaw.length > 0;
        },
        facilitiesLabel() {
            return this.campusTitle
                ? "ADPOLY " + this.campusCode + " Library — Facilities"
                : "Library Facilities";
        },
        facilityItems() {
            return this.facilitiesRaw || [];
        },
        seatingCapacity() {
            if (!this.hasFacilities) return "—";
            var found = this.facilityItems.find(function (i) {
                return i.key === "seating_total";
            });
            return found ? this.formatFacilityValue(found.value) : "—";
        },
        subscribedCount() {
            return this.summary.subscribed_count || 0;
        },
        openAccessCount() {
            return this.summary.open_access_count || 0;
        },
        aiCount() {
            return this.summary.has_ai_count || 0;
        },
        filteredDatabases() {
            var q = (this.searchText || "").trim().toLowerCase();
            var filter = this.activeFilter;
            return this.databases.filter(function (d) {
                if (filter === "subscribed" && d.status !== "subscribed") return false;
                if (filter === "open_access" && d.status !== "open_access") return false;
                if (filter === "ai" && !d.hasAi) return false;
                if (filter === "shared" && !d.sharedWith) return false;
                if (!q) return true;
                var hay =
                    (d.name || "") +
                    " " +
                    (d.description || "") +
                    " " +
                    (d.access || "") +
                    " " +
                    (d.subscriptionLabel || "");
                return hay.toLowerCase().indexOf(q) !== -1;
            });
        },
        statusDonutOptions() {
            return {
                labels: ["Subscribed", "Open Access"],
                colors: ["#4f46e5", "#0f766e"],
                legend: { position: "bottom" },
                dataLabels: { enabled: true },
            };
        },
        statusDonutSeries() {
            return [this.subscribedCount, this.openAccessCount];
        },
        accessBreakdown() {
            var map = {};
            this.databases.forEach(function (d) {
                var key = d.access || "Other";
                if (key.indexOf("ADPOLY Library Portal") === 0) key = "ADPOLY Library Portal";
                map[key] = (map[key] || 0) + 1;
            });
            return Object.keys(map).map(function (k) {
                return { label: k, count: map[k] };
            });
        },
        accessBarOptions() {
            return {
                chart: { toolbar: { show: false } },
                plotOptions: { bar: { borderRadius: 0, columnWidth: "42%", distributed: true } },
                colors: ["#4f46e5", "#0f766e", "#b45309"],
                dataLabels: { enabled: true },
                xaxis: {
                    categories: this.accessBreakdown.map(function (i) {
                        return i.label;
                    }),
                    labels: { rotate: -15, trim: true },
                },
                legend: { show: false },
                grid: { borderColor: "#f1f5f9" },
            };
        },
        accessBarSeries() {
            return [
                {
                    name: "Databases",
                    data: this.accessBreakdown.map(function (i) {
                        return i.count;
                    }),
                },
            ];
        },
        digitalTotal() {
            if (this.settings && this.settings.digital_total != null) {
                return this.settings.digital_total;
            }
            return this.summary.digital_total || 0;
        },
        digitalScope() {
            return this.settings && this.settings.digital_scope
                ? this.settings.digital_scope
                : "ADPOLY AA & AD";
        },
        sortedProviders() {
            return (this.digitalProviders || []).slice().sort(function (a, b) {
                return (b.total || 0) - (a.total || 0);
            });
        },
        digitalByType() {
            return (this.digitalTypes || []).slice().sort(function (a, b) {
                return (b.total || 0) - (a.total || 0);
            });
        },
        printCategories() {
            return this.printCategoriesRaw;
        },
        sortedPrintCategories() {
            var self = this;
            return this.printCategories.slice().sort(function (a, b) {
                return self.getCampusPrintCount(b) - self.getCampusPrintCount(a);
            });
        },
        printGrandTotal() {
            return this.printAa + this.printAd;
        },
        printAa() {
            return this.printCategories.reduce(function (sum, cat) {
                return sum + (cat.aa || 0);
            }, 0);
        },
        printAd() {
            return this.printCategories.reduce(function (sum, cat) {
                return sum + (cat.ad || 0);
            }, 0);
        },
        campusPrintTotal() {
            if (this.summary && this.summary.print_total) {
                return this.summary.print_total;
            }
            var self = this;
            return this.printCategories.reduce(function (sum, cat) {
                return sum + self.getCampusPrintCount(cat);
            }, 0);
        },
        breadCrumbItems() {
            return [
                { label: "Dashboard", path: "/dashboard" },
                { label: "ADPOLY", path: "/adpoly" },
                {
                    label: this.campusTitle || "Campus",
                    path: this.campusKey ? "/adpoly/" + this.campusKey : "/adpoly",
                },
                {
                    label: "Campus",
                    path: this.campusKey ? "/adpoly/" + this.campusKey + "/campus" : "/adpoly",
                },
                { label: "Library" },
            ];
        },
        digitalBarOptions() {
            var cats = this.sortedProviders.map(function (p) {
                return p.name;
            });
            return {
                chart: { toolbar: { show: false }, fontFamily: "inherit" },
                plotOptions: {
                    bar: { horizontal: true, distributed: true, borderRadius: 0, barHeight: "70%" },
                },
                colors: CHART_COLORS,
                dataLabels: {
                    enabled: true,
                    formatter: function (val) {
                        return Number(val).toLocaleString();
                    },
                    style: { fontSize: "10px" },
                },
                xaxis: {
                    categories: cats,
                    labels: {
                        formatter: function (val) {
                            return Number(val).toLocaleString();
                        },
                    },
                },
                legend: { show: false },
                tooltip: {
                    y: {
                        formatter: function (val) {
                            return Number(val).toLocaleString() + " items";
                        },
                    },
                },
                grid: { borderColor: "#f1f5f9" },
            };
        },
        digitalBarSeries() {
            return [
                {
                    name: "Items",
                    data: this.sortedProviders.map(function (p) {
                        return p.total;
                    }),
                },
            ];
        },
        printDonutOptions() {
            var total = this.printGrandTotal;
            return {
                labels: ["AA", "AD"],
                colors: ["#1d4ed8", "#0f766e"],
                legend: { position: "bottom" },
                dataLabels: { enabled: true },
                plotOptions: {
                    pie: {
                        donut: {
                            size: "62%",
                            labels: {
                                show: true,
                                total: {
                                    show: true,
                                    label: "Print items",
                                    formatter: function () {
                                        return Number(total).toLocaleString();
                                    },
                                },
                            },
                        },
                    },
                },
            };
        },
        printDonutSeries() {
            return [this.printAa, this.printAd];
        },
        printCategoryOptions() {
            var cats = this.sortedPrintCategories.map(function (c) {
                return c.label;
            });
            return {
                chart: { toolbar: { show: false } },
                plotOptions: {
                    bar: { horizontal: true, borderRadius: 0, barHeight: "68%" },
                },
                colors: ["#b45309"],
                dataLabels: {
                    enabled: true,
                    formatter: function (val) {
                        return Number(val).toLocaleString();
                    },
                },
                xaxis: { categories: cats },
                legend: { show: false },
                grid: { borderColor: "#f1f5f9" },
            };
        },
        printCategorySeries() {
            var self = this;
            return [
                {
                    name: "Count",
                    data: this.sortedPrintCategories.map(function (c) {
                        return self.getCampusPrintCount(c);
                    }),
                },
            ];
        },
        digitalTypeOptions() {
            var cats = this.digitalByType.map(function (t) {
                return t.label;
            });
            return {
                chart: { toolbar: { show: false }, fontFamily: "inherit" },
                plotOptions: {
                    bar: {
                        horizontal: true,
                        distributed: true,
                        borderRadius: 0,
                        barHeight: "68%",
                    },
                },
                colors: ["#1d4ed8", "#0f766e", "#b45309", "#7c3aed"],
                dataLabels: {
                    enabled: true,
                    formatter: function (val) {
                        return Number(val).toLocaleString();
                    },
                },
                xaxis: {
                    categories: cats,
                    labels: {
                        formatter: function (val) {
                            return Number(val).toLocaleString();
                        },
                    },
                },
                legend: { show: false },
                tooltip: {
                    y: {
                        formatter: function (val) {
                            return Number(val).toLocaleString() + " items";
                        },
                    },
                },
                grid: { borderColor: "#f1f5f9" },
            };
        },
        digitalTypeSeries() {
            return [
                {
                    name: "Items",
                    data: this.digitalByType.map(function (t) {
                        return t.total;
                    }),
                },
            ];
        },
    },
    methods: {
        hasPermission(name) {
            if (this.role === "superadmin") return true;
            return this.permissions && this.permissions.includes(name);
        },
        validateCampus() {
            if (!isValidCampus(this.campus)) {
                this.$router.push({ path: "/notFound" });
            }
        },
        setModuleCount(key, count) {
            var mod = this.modules.find(function (m) {
                return m.key === key;
            });
            if (mod) mod.count = count;
        },
        loadOverview() {
            var self = this;
            if (!this.campusCode) return;
            this.pageLoading = true;
            this.$apollo
                .query({
                    query: OVERVIEW_QUERY,
                    variables: {
                        entity: this.entity,
                        campus: this.campusCode,
                    },
                    fetchPolicy: "network-only",
                })
                .then(function (result) {
                    var payload =
                        result && result.data && result.data.libraryOverview
                            ? result.data.libraryOverview
                            : null;
                    if (!payload || payload.error) {
                        self.toastError(
                            (payload && payload.message) || "Failed to load library overview"
                        );
                        return;
                    }
                    var data = payload.data || {};
                    self.settings = data.settings || null;
                    self.databasesRaw = (data.databases || []).map(mapDatabase);
                    self.digitalTypes = data.digital_holding_types || [];
                    self.digitalProviders = data.digital_holding_providers || [];
                    self.printCategoriesRaw = (data.print_categories || []).map(mapPrintCategory);
                    self.facilitiesRaw = data.facilities || [];
                    self.summary = data.summary || self.summary;

                    self.setModuleCount("Settings", self.settings ? 1 : 0);
                    self.setModuleCount("Facilities", self.summary.facilities_count || 0);
                    self.setModuleCount("Databases", self.summary.databases_count || 0);
                    self.setModuleCount(
                        "DigitalHoldings",
                        (self.digitalTypes.length || 0) + (self.digitalProviders.length || 0)
                    );
                    self.setModuleCount(
                        "PrintHoldings",
                        self.printCategoriesRaw.length || 0
                    );
                })
                .catch(function (err) {
                    self.toastError((err && err.message) || "Failed to load library overview");
                })
                .finally(function () {
                    self.pageLoading = false;
                });
        },
        openModule(module) {
            if (!module) return;
            var permissionMap = {
                Settings: "library-settings-index",
                Facilities: "library-facilities-index",
                Databases: "library-databases-index",
                DigitalHoldings: "library-digital-holdings-index",
                PrintHoldings: "library-print-holdings-index",
            };
            var required = permissionMap[module.key];
            if (required && !this.hasPermission(required) && !this.hasPermission("adpoly-library-index")) {
                if (this.$swal) {
                    this.$swal.fire({
                        icon: "warning",
                        title: "No access",
                        text: "You do not have permission to view this section.",
                        timer: 2500,
                        showConfirmButton: false,
                    });
                }
                return;
            }
            this.activeModule = module;
        },
        closeModule() {
            this.activeModule = null;
        },
        onModuleChanged() {
            this.loadOverview();
        },
        exportAllExcel() {
            var store = this.$store;
            var apolloClient =
                this.$apollo && this.$apollo.provider
                    ? this.$apollo.provider.defaultClient
                    : null;
            if (!apolloClient) return;

            var entity = this.entity;
            var campusCode = this.campusCode;
            var fileName = "ADPOLY_" + campusCode + "_Library_All_Sections.xlsx";
            var totalSheets = getTotalSheetCount();

            store
                .dispatch("exportManager/addJob", {
                    fileName: fileName,
                    totalSheets: totalSheets,
                })
                .then(function (jobId) {
                    store.dispatch("exportManager/setMinimized", false);
                    exportAllLibrarySections(
                        apolloClient,
                        entity,
                        campusCode,
                        function (progress) {
                            store.dispatch("exportManager/updateJob", {
                                id: jobId,
                                progress: progress.percent,
                                currentSheet: progress.sheetName,
                                totalSheets: progress.totalSheets,
                            });
                        }
                    )
                        .then(function (result) {
                            store.dispatch("exportManager/completeJob", {
                                id: jobId,
                                rowCount: result.rowCount,
                            });
                        })
                        .catch(function (error) {
                            store.dispatch("exportManager/failJob", {
                                id: jobId,
                                error: (error && error.message) || "Export failed",
                            });
                        });
                });
        },
        toastError(message) {
            if (!this.$swal) return;
            this.$swal.fire({
                toast: true,
                position: "top-end",
                icon: "error",
                title: message,
                showConfirmButton: false,
                timer: 3500,
                timerProgressBar: true,
            });
        },
        pluralize(unit, count) {
            if (!unit) return "";
            if (count === 1) return unit;
            var lower = unit.toLowerCase();
            var last = lower.charAt(lower.length - 1);
            if (last === "s") return unit;
            if (last === "y" && "aeiou".indexOf(lower.charAt(lower.length - 2)) === -1) {
                return unit.slice(0, -1) + "ies";
            }
            return unit + "s";
        },
        formatNumber(val) {
            if (val === null || val === undefined || val === "—") return "—";
            return Number(val).toLocaleString();
        },
        formatFacilityValue(val) {
            if (val === null || val === undefined || val === "—") return "—";
            if (typeof val === "number") return Number(val).toLocaleString();
            return val;
        },
        formatCampusPrintValue(cat) {
            if (!cat) return "—";
            var value = this.getCampusPrintCount(cat);
            return this.formatNumber(value);
        },
        getCampusPrintCount(cat) {
            if (!cat) return 0;
            if (this.campusCode === "AA") return cat.aa || 0;
            if (this.campusCode === "AD") return cat.ad || 0;
            return (cat.aa || 0) + (cat.ad || 0);
        },
        campusPrintSharePercent(cat) {
            var total = this.campusPrintTotal;
            if (!total) return "0.0";
            return ((this.getCampusPrintCount(cat) / total) * 100).toFixed(1);
        },
        sharePercent(total) {
            if (!this.digitalTotal) return "0.0";
            return ((total / this.digitalTotal) * 100).toFixed(1);
        },
        needsExpand(item) {
            return item && item.description && item.description.length > 280;
        },
        truncatedDescription(item) {
            if (!item || !item.description) return "";
            if (this.expanded[item.id] || !this.needsExpand(item)) return item.description;
            return item.description.slice(0, 280) + "…";
        },
        toggleExpand(id) {
            var next = Object.assign({}, this.expanded);
            next[id] = !next[id];
            this.expanded = next;
        },
    },
};
</script>

<style lang="scss" scoped>
.res-hero {
    background: linear-gradient(135deg, #fff7ed 0%, #ffffff 45%, #f8fafc 100%);
}

.kpi-mini {
    background: rgba(255, 255, 255, 0.92);
    border: 1px solid #e2e8f0;
    border-radius: 0.85rem;
    padding: 0.9rem 1.1rem;
    min-width: 0;
}

.kpi-mini-label {
    font-size: 0.7rem;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}

.kpi-mini-value {
    font-size: 1.35rem;
    font-weight: 700;
    color: #0f172a;
    margin-top: 0.2rem;
    font-variant-numeric: tabular-nums;
}

.portal-btn {
    white-space: nowrap;
}

.lib-export-btn {
    border: 1px solid rgba(16, 185, 129, 0.3);
    background: linear-gradient(to bottom, #ffffff, rgba(16, 185, 129, 0.08));
    color: #064e3b;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.lib-export-btn:hover {
    border-color: rgba(16, 185, 129, 0.5);
}

.lib-export-btn:disabled {
    opacity: 0.55;
    pointer-events: none;
}

.lib-module {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    text-align: left;
    padding: 1rem;
    border-radius: 1rem;
    border: 1px solid #e2e8f0;
    background: #f8fafc;
    min-height: 7rem;
    transition: all 0.15s ease;
}

.lib-module:hover {
    background: #fff;
    border-color: #c7d2fe;
    box-shadow: 0 8px 20px -12px rgba(79, 70, 229, 0.35);
}

.lib-module__icon {
    font-size: 1.5rem;
    margin-bottom: 0.45rem;
}

.lib-module__label {
    font-size: 0.875rem;
    font-weight: 650;
    color: #0f172a;
}

.lib-module__count {
    margin-top: 0.35rem;
    font-size: 0.7rem;
    font-weight: 600;
    color: #4338ca;
    background: #eef2ff;
    border-radius: 9999px;
    padding: 0.15rem 0.5rem;
}

.facility-card {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 1rem;
    border-radius: 0.9rem;
    border: 1px solid #e2e8f0;
    background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
}

.facility-icon {
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 0.75rem;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #fff7ed;
    color: #c2410c;
    flex-shrink: 0;
}


.stat-tile {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 0.75rem;
    padding: 0.6rem 0.85rem;
    min-width: 5rem;
}

.stat-label {
    font-size: 0.65rem;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}

.stat-value {
    font-size: 1.15rem;
    font-weight: 700;
    color: #0f172a;
}

.filter-chip {
    padding: 0.4rem 0.85rem;
    border-radius: 9999px;
    border: 1px solid #e2e8f0;
    background: #fff;
    font-size: 0.75rem;
    color: #475569;
}

.filter-chip.active {
    background: #4f46e5;
    border-color: #4f46e5;
    color: #fff;
}

.status-pill {
    font-size: 0.65rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    padding: 0.25rem 0.55rem;
    border-radius: 9999px;
}

.status-sub {
    background: #eef2ff;
    color: #3730a3;
}

.status-open {
    background: #ccfbf1;
    color: #115e59;
}

.badge {
    display: inline-flex;
    align-items: center;
    padding: 0.25rem 0.55rem;
    border-radius: 0.5rem;
    font-size: 0.7rem;
    font-weight: 600;
}

.badge-ai {
    background: #eef2ff;
    color: #4338ca;
}

.badge-shared {
    background: #fff7ed;
    color: #c2410c;
}

.badge-access {
    background: #f1f5f9;
    color: #475569;
}

.db-card {
    border: 1px solid #e5e7eb;
    border-radius: 1rem;
    padding: 1.15rem;
    background: #fff;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}
</style>
